import React, { useCallback, useEffect, useState } from 'react';

function AdminErrorLog({ token }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [unresolved, setUnresolved] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState({ resolved: 'false', endpoint: '', from: '', to: '' });

  const headers = useCallback(() => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }), [token]);

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const q = new URLSearchParams();
      if (filters.resolved) q.set('resolved', filters.resolved);
      if (filters.endpoint.trim()) q.set('endpoint', filters.endpoint.trim());
      if (filters.from) q.set('from', filters.from);
      if (filters.to) q.set('to', filters.to);
      q.set('limit', '200');
      const res = await fetch(`/api/v1/error-log?${q.toString()}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load error log');
      setRows(data.rows || []);
      setTotal(data.total || 0);
      setUnresolved(data.unresolved || 0);
    } catch (e) {
      setError(e?.message || 'Failed to load error log');
    } finally {
      setLoading(false);
    }
  }, [filters, headers]);

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [token]);

  const toggleResolve = async (row) => {
    try {
      const res = await fetch(`/api/v1/error-log/${row.id}/resolve`, { method: 'PATCH', headers: headers(), body: JSON.stringify({ resolved: !row.resolved }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');
      await load();
    } catch (e) { setError(e?.message || 'Update failed'); }
  };

  const clearResolved = async () => {
    if (!window.confirm('Delete all RESOLVED error-log entries? This cannot be undone.')) return;
    try {
      const res = await fetch('/api/v1/error-log?scope=RESOLVED', { method: 'DELETE', headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Clear failed');
      setMessage(`Cleared ${data.deleted} resolved entr${data.deleted === 1 ? 'y' : 'ies'}.`);
      await load();
    } catch (e) { setError(e?.message || 'Clear failed'); }
  };

  const openDetail = async (id) => {
    try {
      const res = await fetch(`/api/v1/error-log/${id}`, { headers: headers() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to load detail');
      setDetail(data);
    } catch (e) { setError(e?.message || 'Failed to load detail'); }
  };

  const th = { padding: '0.5rem 0.6rem', textAlign: 'left', fontWeight: 700, whiteSpace: 'nowrap' };
  const td = { padding: '0.45rem 0.6rem', verticalAlign: 'top', fontSize: '0.82rem' };
  const inputStyle = { padding: '0.45rem 0.55rem', border: '1px solid #cbd5e0', borderRadius: '6px', fontSize: '0.82rem' };

  return (
    <div className="page-shell">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h2 style={{ color: 'var(--theme-primary-dark)', margin: 0 }}>
          Application Error Log
          {unresolved > 0 && <span style={{ marginLeft: '0.6rem', fontSize: '0.8rem', background: '#fee2e2', color: '#b91c1c', borderRadius: '999px', padding: '0.15rem 0.6rem', fontWeight: 700 }}>{unresolved} unresolved</span>}
        </h2>
        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>{total} matching entr{total === 1 ? 'y' : 'ies'}</div>
      </div>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar" style={{ marginTop: '0.8rem', display: 'flex', flexWrap: 'wrap', gap: '0.6rem', alignItems: 'flex-end' }}>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.75rem', color: '#475569' }}>Status
          <select value={filters.resolved} onChange={(e) => setFilters({ ...filters, resolved: e.target.value })} style={inputStyle}>
            <option value="false">Unresolved</option>
            <option value="true">Resolved</option>
            <option value="">All</option>
          </select>
        </label>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.75rem', color: '#475569' }}>Endpoint contains
          <input value={filters.endpoint} onChange={(e) => setFilters({ ...filters, endpoint: e.target.value })} placeholder="/kot/masters/menu-items" style={{ ...inputStyle, minWidth: '220px' }} />
        </label>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.75rem', color: '#475569' }}>From
          <input type="date" value={filters.from} onChange={(e) => setFilters({ ...filters, from: e.target.value })} style={inputStyle} />
        </label>
        <label style={{ display: 'grid', gap: '0.2rem', fontSize: '0.75rem', color: '#475569' }}>To
          <input type="date" value={filters.to} onChange={(e) => setFilters({ ...filters, to: e.target.value })} style={inputStyle} />
        </label>
        <button onClick={load} disabled={loading} style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: 'none', background: 'var(--theme-primary)', color: '#fff', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
          {loading ? 'Loading…' : 'Apply / Refresh'}
        </button>
        <button onClick={clearResolved} style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', fontWeight: 600, cursor: 'pointer', fontSize: '0.82rem' }}>
          Clear Resolved
        </button>
      </div>

      <div className="filter-bar" style={{ overflowX: 'auto' }}>
        {rows.length === 0 ? (
          <p style={{ color: '#64748b' }}>No errors match the current filter. {filters.resolved === 'false' ? '🎉 Nothing unresolved.' : ''}</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff', minWidth: '900px' }}>
            <thead>
              <tr style={{ background: 'var(--theme-primary-dark)', color: '#fff' }}>
                <th style={th}>Time</th>
                <th style={th}>Method</th>
                <th style={th}>Endpoint</th>
                <th style={th}>Status</th>
                <th style={th}>Message</th>
                <th style={th}>User</th>
                <th style={th}>Property</th>
                <th style={th}>Status</th>
                <th style={th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid #e2e8f0', background: r.resolved ? '#f8fafc' : '#fff' }}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{(r.created_at || '').replace('T', ' ').slice(0, 19)}</td>
                  <td style={td}><span style={{ fontFamily: 'monospace', fontSize: '0.76rem' }}>{r.method}</span></td>
                  <td style={{ ...td, maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.endpoint}>{r.endpoint}</td>
                  <td style={td}><span style={{ color: r.status_code >= 500 ? '#b91c1c' : '#b45309', fontWeight: 700 }}>{r.status_code}</span></td>
                  <td style={{ ...td, maxWidth: '320px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.error_message}>{r.error_message}</td>
                  <td style={td}>{r.username || '—'}</td>
                  <td style={{ ...td, fontSize: '0.78rem' }} title={r.property_id || ''}>{r.property_name || (r.property_id ? String(r.property_id).slice(0, 8) : '—')}</td>
                  <td style={td}>
                    <button onClick={() => toggleResolve(r)} style={{ padding: '0.25rem 0.5rem', borderRadius: '5px', border: 'none', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600, background: r.resolved ? '#dcfce7' : '#fee2e2', color: r.resolved ? '#166534' : '#b91c1c' }}>
                      {r.resolved ? '✓ Resolved' : 'Open'}
                    </button>
                  </td>
                  <td style={td}>
                    <button onClick={() => openDetail(r.id)} style={{ padding: '0.25rem 0.6rem', borderRadius: '5px', border: '1px solid #cbd5e0', background: '#fff', cursor: 'pointer', fontSize: '0.72rem' }}>View</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.42)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }} onClick={() => setDetail(null)}>
          <div style={{ width: 'min(760px,100%)', maxHeight: '88vh', overflowY: 'auto', background: '#fff', borderRadius: '10px', border: '1px solid #d9e2ec', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.7rem 1rem', borderBottom: '1px solid #e2e8f0' }}>
              <h4 style={{ margin: 0, color: 'var(--theme-primary)' }}>Error #{detail.id}</h4>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1rem', display: 'grid', gap: '0.6rem', fontSize: '0.85rem' }}>
              <div><b>When:</b> {(detail.created_at || '').replace('T', ' ').slice(0, 19)}</div>
              <div><b>Request:</b> <span style={{ fontFamily: 'monospace' }}>{detail.method} {detail.endpoint}</span> → <span style={{ color: '#b91c1c', fontWeight: 700 }}>{detail.status_code}</span></div>
              <div><b>User:</b> {detail.username || '—'} &nbsp; <b>IP:</b> {detail.ip || '—'}</div>
              <div><b>Chain / Property:</b> {detail.chain_id || '—'} / {detail.property_name ? `${detail.property_name} (${detail.property_id})` : (detail.property_id || '—')}</div>
              <div>
                <b>Message</b>
                <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '0.6rem', margin: '0.3rem 0 0', fontSize: '0.8rem', color: '#991b1b' }}>{detail.error_message}</pre>
              </div>
              {detail.stack && (
                <div>
                  <b>Stack</b>
                  <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0.6rem', margin: '0.3rem 0 0', fontSize: '0.74rem', color: '#475569', maxHeight: '260px', overflowY: 'auto' }}>{detail.stack}</pre>
                </div>
              )}
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.3rem' }}>
                <button onClick={() => { toggleResolve(detail); setDetail(null); }} style={{ padding: '0.5rem 0.9rem', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: '0.82rem', background: detail.resolved ? '#fee2e2' : '#dcfce7', color: detail.resolved ? '#b91c1c' : '#166534' }}>
                  {detail.resolved ? 'Mark as Open' : 'Mark as Resolved'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminErrorLog;
